import {startTransition, useMemo, useRef, useState} from 'react'

import {
  Combobox as ComboboxPrimitive,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxProvider,
  ComboboxDisclosure,
} from '@ariakit/react'
import * as RadixPopover from '@radix-ui/react-popover'
import classNames from 'classnames'
import styled from 'styled-components'

import {styledTheme} from '@festi/common/themes'
import {styleScrollbar} from '@festi/common/styles'
import {normalizeSearchString} from '@festi/utils/strings'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled(ComboboxLabel)`
  max-width: 100%;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  color: ${styledTheme.palette.blue};
  pointer-events: none;
`

const TriggerInput = styled(ComboboxPrimitive)`
  width: 100%;
  height: 48px;
  background-color: ${styledTheme.palette.white};
  border: 1px solid ${styledTheme.palette.grey};
  border-radius: 8px;
  padding-inline: 16px;
  padding-block: 13px;
  color: ${styledTheme.palette.blue};
  font-size: 16px;
  font-weight: 500;
  line-height: normal;

  &::placeholder {
    color: ${styledTheme.palette.blue};
  }

  &:focus-visible,
  &:hover {
    border-color: ${styledTheme.palette.ui30Solid};
  }
  &:focus {
    outline: none;
  }
`

const DropdownButton = styled(ComboboxDisclosure)`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: ${styledTheme.palette.ui30Solid};
  transition: transform 150ms ease-in-out;
  cursor: pointer;

  &.open {
    transform: rotate(180deg);
  }
`
const ListWrapper = styled.div`
  width: var(--radix-popover-trigger-width);
  z-index: ${styledTheme.zIndex.modal};
  background-color: ${styledTheme.palette.white};
  border: 1px solid ${styledTheme.palette.grey};
  border-radius: 8px;
  box-shadow: ${styledTheme.boxShadow.simpleSubtle};
  overflow: hidden;
`
const List = styled(ComboboxList)`
  max-height: 368px;
  overflow-y: auto;

  ${styleScrollbar}
`

const ListItem = styled(ComboboxItem)`
  background-color: ${styledTheme.palette.white};
  border-bottom: 1px solid ${styledTheme.palette.ui10Solid};
  height: 46px;
  width: 100%;
  padding-inline: 16px;
  padding-block: 12px;
  color: ${styledTheme.palette.blue};
  font-size: 16px;
  font-weight: 400;
  transition: background-color 10ms ease-in dissolve;
  user-select: none;
  cursor: pointer;

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &[data-active-item] {
    background-color: ${styledTheme.palette.primaryLightestBlue};
  }
`

const EmptyList = styled.div`
  background-color: ${styledTheme.palette.white};
  height: 46px;
  width: 100%;
  padding-inline: 16px;
  padding-block: 12px;
  color: ${styledTheme.palette.blue};
  font-size: 16px;
  font-weight: 400;
`

interface Props {
  label: string
  placeholder: string
  items: {label: string; value: string}[]
  onSelect: (value: string) => void
}

export default function Combobox({label, placeholder, items, onSelect}: Props) {
  const comboboxRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const disclosureRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const matches = useMemo(
    () =>
      items.filter((item) =>
        normalizeSearchString(item.value).includes(
          normalizeSearchString(searchValue),
        ),
      ),
    [items, searchValue],
  )

  return (
    <Container>
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <ComboboxProvider
          open={open}
          setOpen={setOpen}
          setValue={(value) => {
            startTransition(() => setSearchValue(value))
          }}
        >
          <Label>{label}</Label>
          <RadixPopover.Anchor asChild>
            <div style={{position: 'relative'}}>
              <TriggerInput ref={comboboxRef} placeholder={placeholder} />
              <DropdownButton
                ref={disclosureRef}
                className={classNames({open})}
              />
            </div>
          </RadixPopover.Anchor>
          <RadixPopover.Portal>
            <RadixPopover.Content
              asChild
              sideOffset={2}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                const target = event.target as Element | null
                const isCombobox = target === comboboxRef.current
                const inListbox = target && listboxRef.current?.contains(target)
                const isDisclosure = target === disclosureRef.current

                if (isDisclosure) {
                  event.preventDefault()
                  comboboxRef.current?.blur()
                }
                if (isCombobox || inListbox) {
                  event.preventDefault()
                }
              }}
            >
              <ListWrapper>
                <List ref={listboxRef} role="listbox">
                  {!!matches.length ? (
                    matches.map((item) => (
                      <ListItem
                        focusOnHover
                        key={item.value}
                        value={item.value}
                        onClick={() => {
                          onSelect(item.value)
                          setOpen(false)
                        }}
                      >
                        {item.label}
                      </ListItem>
                    ))
                  ) : (
                    <EmptyList>Engin niðurstaða</EmptyList>
                  )}
                </List>
              </ListWrapper>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </ComboboxProvider>
      </RadixPopover.Root>
    </Container>
  )
}
